import { Button } from '@/components/common/button'
import { NetworkImage } from '@/components/common/networkImage'
import { Popover, PopoverContent, PopoverList, PopoverListItem, PopoverTrigger } from '@/components/common/popover'
import { TokenImage } from '@/components/common/tokenImage'
import { ArrowIcon } from '@/components/icons/arrow'
import type { CrosschainOption } from '@/types/crosschainOption'
import { cn } from '@/utils/cn'
import { getNetworkName } from '@/utils/network/getNetworkName'
import type { Network } from '@vaultsfyi/common/types'
import { formatPreciseTokenValue } from '@vaultsfyi/common/utils'
import { useState } from 'react'

interface CrosschainAssetSelectProps {
  asset: {
    symbol: string
    decimals: number
  }
  network: {
    name: Network
  }
  selectedInput: CrosschainOption | null
  crosschainOptions: CrosschainOption[]
  setSelectedInput: (option: CrosschainOption) => void
  className?: string
}

export const CrosschainAssetSelect = ({
  asset,
  network,
  selectedInput,
  crosschainOptions,
  setSelectedInput,
  className,
}: CrosschainAssetSelectProps) => {
  const [open, setOpen] = useState(false)

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="default"
          size="default"
          className={cn(
            'group flex h-full max-w-full items-center gap-1 overflow-hidden px-1',
            'rounded-xl border bg-page py-0.5 outline-none transition-colors',
            className
          )}
        >
          <div className="relative">
            <TokenImage symbol={asset.symbol} className="size-7 p-0.5" />
            <NetworkImage
              network={selectedInput?.network ?? network.name}
              className="absolute right-0 bottom-0 size-[18px] translate-x-0.5 translate-y-0.5 border"
            />
          </div>
          <div className="flex max-w-full flex-col items-start truncate">
            <span className="max-w-full truncate text-heading-foreground" title={selectedInput?.symbol ?? asset.symbol}>
              {selectedInput?.symbol ?? asset.symbol}
            </span>
            {selectedInput ? (
              <span className="max-w-full truncate text-[10px] text-body-foreground capitalize">
                {getNetworkName(selectedInput.network)}
              </span>
            ) : null}
          </div>
          <div className="group-data-[state=open]:-scale-y-100 ml-auto transition-transform">
            <ArrowIcon direction="down" size={20} />
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-fit p-0" align="end" sideOffset={6}>
        <PopoverList>
          {crosschainOptions.map((option) => (
            <PopoverListItem key={`${option.symbol}-${option.network}`}>
              <Button
                variant="default"
                size="default"
                className={cn(
                  'flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-heading-foreground',
                  option.address === selectedInput?.address && option.network === selectedInput?.network
                    ? 'bg-surface'
                    : 'hover:bg-surface'
                )}
                onClick={() => {
                  setSelectedInput(option)
                  setOpen(false)
                }}
              >
                <span className="capitalize">{getNetworkName(option.network)}</span>
                <span className="ml-auto">
                  {formatPreciseTokenValue({
                    value: option.balanceNative.toString(),
                    decimals: asset.decimals,
                  })}{' '}
                  {option.symbol}
                </span>
                <div className="relative">
                  <TokenImage symbol={option.symbol} className="size-7 p-0.5" />
                  <NetworkImage
                    network={option.network}
                    className="absolute right-0 bottom-0 size-[18px] translate-x-0.5 translate-y-0.5 border"
                  />
                </div>
              </Button>
            </PopoverListItem>
          ))}
        </PopoverList>
      </PopoverContent>
    </Popover>
  )
}
